import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useWallet } from './WalletContext';
import { useContracts } from '../hooks/useContract';

const UserDataContext = createContext(null);

export const useUserData = () => {
  const ctx = useContext(UserDataContext);
  if (!ctx) throw new Error('useUserData must be used within UserDataProvider');
  return ctx;
};

// Provider — fetches user data ONCE per account change, shares across all pages.
// This means navigating between /packages, /team, /royalty etc. doesn't re-flash the
// "Checking Registration" loader every time.
export const UserDataProvider = ({ children }) => {
  const { account } = useWallet();
  const { storage } = useContracts();

  const [userId, setUserId] = useState(null);
  const [user, setUser] = useState(null);
  const [income, setIncome] = useState(null);
  const [heldBucket, setHeldBucket] = useState(null);
  const [heldTarget, setHeldTarget] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);

  const runIdRef = useRef(0);
  const lastLoadedAccountRef = useRef(null);

  const load = async () => {
    if (!account || !storage) {
      setUserId(null); setUser(null); setIncome(null); setHeldBucket(null); setHeldTarget(0);
      setHasLoaded(true);
      lastLoadedAccountRef.current = null;
      return;
    }
    const myRun = ++runIdRef.current;
    setLoading(true); setError(null);

    try {
      const id = await storage.addressToId(account);
      if (myRun !== runIdRef.current) return;
      const numId = id.toNumber ? id.toNumber() : Number(id);

      if (numId === 0) {
        setUserId(0);
        setUser(null);
        setIncome(null);
        setHeldBucket(null);
        setHeldTarget(0);
        setLoading(false);
        setHasLoaded(true);
        lastLoadedAccountRef.current = account;
        return;
      }

      const [u, breakdown, held, target] = await Promise.all([
        storage.users(numId),
        storage.userIncomeBreakdown(numId),
        storage.heldBucket(numId),
        storage.heldBucketTargetPkg(numId)
      ]);

      if (myRun !== runIdRef.current) return;

      setUserId(numId);
      setUser({
        wallet: u[0],
        id: u[1].toNumber ? u[1].toNumber() : Number(u[1]),
        referrerId: u[2].toNumber ? u[2].toNumber() : Number(u[2]),
        serialNumber: u[3].toNumber ? u[3].toNumber() : Number(u[3]),
        joinTime: u[4].toNumber ? u[4].toNumber() : Number(u[4]),
        isBlocked: u[5],
        registrationType: u[6],
        totalDirectReferrals: u[7].toNumber ? u[7].toNumber() : Number(u[7]),
        topPackageId: u[8].toNumber ? u[8].toNumber() : Number(u[8]),
        totalClaimed: u[9],
        totalIncome: u[10]
      });
      setIncome({
        directReferralIncome: breakdown[0],
        levelIncome: breakdown[1],
        royaltyIncome: breakdown[2]
      });
      setHeldBucket(held);
      setHeldTarget(target.toNumber ? target.toNumber() : Number(target));
      lastLoadedAccountRef.current = account;
    } catch (e) {
      if (myRun === runIdRef.current) setError(e);
    } finally {
      if (myRun === runIdRef.current) {
        setLoading(false);
        setHasLoaded(true);
      }
    }
  };

  // Only re-fetch when the account itself changes to a different address.
  // Prevents re-runs from wallet extensions bouncing events.
  useEffect(() => {
    if (account !== lastLoadedAccountRef.current) {
      load();
    }
    // eslint-disable-next-line
  }, [account]);

  const value = {
    userId,
    user,
    income,
    heldBucket,
    heldTarget,
    loading,
    initialLoading: loading && !hasLoaded,
    hasLoaded,
    error,
    isRegistered: userId !== null && userId > 0,
    refresh: load
  };

  return <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>;
};
